import { PageProvider } from "@src/components/PageProvider";
import Breadcrumbs from "@src/components/atoms/Breadcrumbs/Breadcrumbs";
import ShareButtons from "@src/components/molecules/ShareButtons";
import ArticleSchema from "@src/components/schemas/ArticleSchema";
import ErrorPage from "next/error";
import Head from "next/head";
import { useRouter } from "next/router";
import React, { ReactElement, useEffect } from "react";
import { PageSeo } from "src/components/SEO";
import Footnote from "src/components/atoms/Footnote";
import LeadFormSection from "src/components/forms/LeadForm";
import { BlockRenderer } from "src/components/molecules/BlockRenderer";
import PostHeader from "src/components/molecules/PostHeader";
import Faq from "src/components/organisms/Faq";
import RichContent from "src/components/organisms/RichContent";
import TwoStackColumn from "src/components/organisms/TwoStackColumn";
import siteMetadata from "src/data/siteMetadata";
import { PostContentElement } from "src/services/contentful/post/postParser";
import {
  IAuthorFields,
  IPageGenericPageFields,
  IPostFields,
} from "src/types/generated/contentful";

declare global {
  interface Window {
    dataLayer: any[] | undefined;
    blueshift: any;
    blueshiftProductId: string | undefined;
  }
}

const ContentTypeToComponentMap = {
  richContent: RichContent,
  twoStackColumn: TwoStackColumn,
  faq: Faq,
};

export type GenericPagePageProps = {
  page: IPageGenericPageFields;
  post: IPostFields;
};

// TODO: add back the generic page props remove any

// TODO: ATTN: REMOVE ALL INSTANCES OF OLD POST WHEN OLD POST OBJECT IS TRANSITIONED
const BlogPostPage = (data: any): ReactElement => {
  const { page, settings, header, footerMenu, breadcrumbs } = data;
  const router = useRouter();

  const oldPost = data.post;

  let slug = page?.fields?.slug;

  let seoTitle = page?.fields?.seo?.fields?.title ?? page?.fields?.title;
  let seoDescription = page?.fields?.seo?.fields?.description;
  let seoImages = page?.fields?.seo?.fields?.images;
  let noIndex = page?.fields?.seo?.fields?.no_index;
  let noFollow = page?.fields?.seo?.fields?.no_follow;
  let content = page?.fields?.content;
  const parentPageSlug = page?.fields?.parentPage?.fields?.slug;

  const video = page?.fields?.seo?.fields?.videos
    ? page?.fields?.seo?.fields?.videos[0]
    : undefined;
  const pageOrOldPost = oldPost ? { ...oldPost } : page;

  let blueshiftProductId: string = page?.fields?.productType?.fields?.productId;

  if (oldPost) {
    slug = oldPost?.slug;
    seoTitle = oldPost?.title;
    seoDescription = oldPost?.excerpt;
    seoImages = [{ fields: { file: { url: oldPost?.coverImage } } }];
    blueshiftProductId = oldPost?.blueshiftProductId;
    content = oldPost?.contents;
  }

  if (!slug) {
    console.error("ERROR: no slug found", JSON.stringify(page));
  }

  if (
    seoDescription &&
    seoDescription.length > 0 &&
    seoDescription.indexOf("*** DEPRECATED ***") > -1
  ) {
    // fix issue where *** DEPRECATED *** showed up in the excerpt on accident for shd
    seoDescription = page?.fields?.seo?.fields?.description;
  }

  useEffect(() => {
    if (typeof blueshiftProductId !== "undefined") {
      window.dataLayer = window.dataLayer || [];

      if (window.blueshift) {
        window.dataLayer.push({
          event: "blueshiftPageView",
          blueshiftProductId,
        });
        console.log("Fired bsPageView:", blueshiftProductId);
      } else {
        window.dataLayer.push({
          blueshiftProductId,
        });
        console.log("Fired GA Only Err no BS:", blueshiftProductId);
      }
    }
  }, [blueshiftProductId]);

  if (!router.isFallback && !page && !oldPost) {
    return <ErrorPage statusCode={404} />;
  }

  if (router.isFallback) {
    return <div>Loading…</div>;
  }

  // console.log(
  //   "customContactForm",
  //   JSON.stringify(content?.fields?.customContactForm?.fields)
  // );

  let updatedAt = page?.sys?.updatedAt;
  let pubDate = page?.fields?.pubDate;
  try {
    if (updatedAt) {
      updatedAt = new Date(updatedAt).toISOString();
    }
  } catch (e) {}
  try {
    if (pubDate) {
      pubDate = new Date(pubDate).toISOString();
    }
  } catch (e) {}

  const author = page?.fields?.content?.fields?.author?.fields as IAuthorFields;

  return (
    <PageProvider slug={slug} parentPageSlug={parentPageSlug}>
      <Head>
        <title>{seoTitle}</title>
      </Head>
      <PageSeo
        url={`${siteMetadata.mainSiteUrl}/${parentPageSlug}/${slug}`}
        title={seoTitle}
        description={seoDescription}
        images={seoImages}
        nofollow={noFollow}
        noindex={noIndex}
        video={video}
        dateModified={updatedAt}
        datePublished={pubDate}
        authorName={author?.name}
        authorPicture={page?.fields?.seo?.fields?.authorPicture}
      />
      <article>
        <ArticleSchema
          url={`${siteMetadata.mainSiteUrl}/${parentPageSlug}/${slug}`}
          title={seoTitle}
          description={seoDescription}
          images={seoImages}
          organization={settings?.organization}
          dateModified={new Date(page?.sys?.updatedAt).toISOString()}
          datePublished={new Date(page?.sys?.createdAt).toISOString()}
          authorName={author?.name}
        />
        <Breadcrumbs breadcrumbs={breadcrumbs} />

        <PostHeader post={pageOrOldPost} settings={settings} />
        <div>
          {oldPost ? (
            oldPost.contents.map((element: PostContentElement) => {
              const Component = ContentTypeToComponentMap[element.contentType];
              // @ts-ignore
              return <Component key={element.id} {...element.parsed} />;
            })
          ) : (
            <BlockRenderer
              block={content?.fields?.content}
              layoutId={page ? page["data-layoutid"] : "blogContent"}
            />
          )}
        </div>
        <ShareButtons
          description={seoDescription}
          title={seoTitle}
          images={seoImages}
          slug={slug}
          key="bottom-share-buttons"
          preCopy="Share this page: "
          data={settings?.shareButtonDefault}
        />

        {content?.fields?.customContactForm ? (
          <BlockRenderer
            block={content?.fields?.customContactForm}
            layoutId={"customContactForm"}
          />
        ) : null}
        {/* MOVE THIS TO SITE SETTINGS
        // ) : (
        //   <LeadFormSection
        //     formVerticalAlignment={"Top"}
        //     formHorizontalAlignment={"Left"}
        //   />
        // )
        */}

        {content?.fields?.footnote ? (
          <BlockRenderer
            block={content?.fields?.footnote}
            layoutId={"footnote"}
          />
        ) : (
          <Footnote />
        )}
      </article>

      <footer>
        <BlockRenderer layoutId="footer" block={footerMenu} />
      </footer>
    </PageProvider>
  );
};

export default React.memo(BlogPostPage);
